/**
 * Cerimônia WebAuthn de registro com normalização das options do Supabase Auth.
 *
 * O GoTrue omite `requireResidentKey` quando `residentKey: "required"` (spec Level 2+).
 * O classificador do @supabase/auth-js só trata ConstraintError se `requireResidentKey === true`,
 * o que produz o genérico ERROR_PASSTHROUGH / "a Non-Webauthn related error has occurred".
 * Também evitamos o bug de `.buffer` em Uint8Array (byteOffset/byteLength) no fallback manual.
 */

export type PasskeyCreationOptionsJSON = {
  rp: { name: string; id?: string }
  user: { id: string; name: string; displayName: string }
  challenge: string
  pubKeyCredParams: Array<{ type: string; alg: number }>
  timeout?: number
  excludeCredentials?: Array<{
    id: string
    type?: string
    transports?: string[]
  }>
  authenticatorSelection?: {
    authenticatorAttachment?: string
    requireResidentKey?: boolean
    residentKey?: string
    userVerification?: string
  }
  attestation?: string
  extensions?: Record<string, unknown>
}

export type PasskeyRegistrationResponseJSON = {
  id: string
  rawId: string
  type: string
  response: {
    clientDataJSON: string
    attestationObject: string
    transports?: string[]
  }
  clientExtensionResults: AuthenticationExtensionsClientOutputs
  authenticatorAttachment?: string
}

const base64UrlToUint8Array = (value: string): Uint8Array => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const padLength = (4 - (padded.length % 4)) % 4
  const base64 = padded + '='.repeat(padLength)
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]!)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

const copyToArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return copy.buffer
}

/**
 * Alinha options do servidor com WebAuthn Level 1+2:
 * `residentKey: "required"` ⇒ `requireResidentKey: true`.
 */
export const normalizePasskeyCreationOptions = (
  options: PasskeyCreationOptionsJSON,
): PasskeyCreationOptionsJSON => {
  const selection = options.authenticatorSelection
  if (!selection) return options

  const residentKey = selection.residentKey
  const requireResidentKey =
    selection.requireResidentKey ?? (residentKey === 'required' ? true : residentKey ? false : undefined)

  if (requireResidentKey === selection.requireResidentKey) return options

  return {
    ...options,
    authenticatorSelection: {
      ...selection,
      requireResidentKey,
    },
  }
}

export const deserializePasskeyCreationOptions = (
  optionsJson: PasskeyCreationOptionsJSON,
): PublicKeyCredentialCreationOptions => {
  const options = normalizePasskeyCreationOptions(optionsJson)

  if (
    typeof PublicKeyCredential !== 'undefined' &&
    'parseCreationOptionsFromJSON' in PublicKeyCredential &&
    typeof (
      PublicKeyCredential as unknown as {
        parseCreationOptionsFromJSON?: (json: PasskeyCreationOptionsJSON) => PublicKeyCredentialCreationOptions
      }
    ).parseCreationOptionsFromJSON === 'function'
  ) {
    return (
      PublicKeyCredential as unknown as {
        parseCreationOptionsFromJSON: (json: PasskeyCreationOptionsJSON) => PublicKeyCredentialCreationOptions
      }
    ).parseCreationOptionsFromJSON(options)
  }

  const result: PublicKeyCredentialCreationOptions = {
    rp: options.rp,
    user: {
      ...options.user,
      id: copyToArrayBuffer(base64UrlToUint8Array(options.user.id)),
    },
    challenge: copyToArrayBuffer(base64UrlToUint8Array(options.challenge)),
    pubKeyCredParams: options.pubKeyCredParams as PublicKeyCredentialParameters[],
    timeout: options.timeout,
    attestation: options.attestation as AttestationConveyancePreference | undefined,
    authenticatorSelection: options.authenticatorSelection as
      | AuthenticatorSelectionCriteria
      | undefined,
    extensions: options.extensions as AuthenticationExtensionsClientInputs | undefined,
  }

  if (options.excludeCredentials?.length) {
    result.excludeCredentials = options.excludeCredentials.map((cred) => ({
      id: copyToArrayBuffer(base64UrlToUint8Array(cred.id)),
      type: (cred.type || 'public-key') as PublicKeyCredentialType,
      transports: cred.transports as AuthenticatorTransport[] | undefined,
    }))
  }

  return result
}

export const serializePasskeyCreationResponse = (
  credential: PublicKeyCredential,
): PasskeyRegistrationResponseJSON => {
  if ('toJSON' in credential && typeof credential.toJSON === 'function') {
    return (credential as PublicKeyCredential & { toJSON: () => PasskeyRegistrationResponseJSON }).toJSON()
  }

  const attestation = credential.response as AuthenticatorAttestationResponse
  return {
    id: credential.id,
    rawId: credential.id,
    type: credential.type,
    response: {
      clientDataJSON: bytesToBase64Url(new Uint8Array(attestation.clientDataJSON)),
      attestationObject: bytesToBase64Url(new Uint8Array(attestation.attestationObject)),
      transports:
        typeof attestation.getTransports === 'function' ? attestation.getTransports() : undefined,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
  }
}

type IdentifiedPasskeyError = Error & {
  code: string
  cause?: unknown
}

const isResidentKeyRequired = (publicKey: PublicKeyCredentialCreationOptions): boolean => {
  const selection = publicKey.authenticatorSelection
  if (!selection) return false
  if (selection.requireResidentKey === true) return true
  return selection.residentKey === 'required'
}

/**
 * Classifica erros de `navigator.credentials.create` com as correções em relação ao SDK Supabase.
 */
export const identifyPasskeyRegistrationError = (
  error: Error,
  publicKey: PublicKeyCredentialCreationOptions,
  signal?: AbortSignal,
): IdentifiedPasskeyError => {
  const wrap = (message: string, code: string, name?: string): IdentifiedPasskeyError => {
    const wrapped = new Error(message, { cause: error }) as IdentifiedPasskeyError
    wrapped.name = name ?? (error.name || 'WebAuthnError')
    wrapped.code = code
    return wrapped
  }

  if (error.name === 'AbortError' && signal instanceof AbortSignal) {
    return wrap('Registration ceremony was sent an abort signal', 'ERROR_CEREMONY_ABORTED')
  }

  if (error.name === 'ConstraintError') {
    if (isResidentKeyRequired(publicKey)) {
      return wrap(
        'Discoverable credentials were required but no available authenticator supported it',
        'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT',
      )
    }
    if (publicKey.authenticatorSelection?.userVerification === 'required') {
      return wrap(
        'User verification was required but no available authenticator supported it',
        'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT',
      )
    }
  }

  if (error.name === 'InvalidStateError') {
    return wrap(
      'The authenticator was previously registered',
      'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED',
    )
  }

  if (error.name === 'NotAllowedError') {
    return wrap(error.message || 'NotAllowedError', 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY')
  }

  if (error.name === 'NotSupportedError') {
    const validParams = (publicKey.pubKeyCredParams ?? []).filter((param) => param.type === 'public-key')
    if (validParams.length === 0) {
      return wrap(
        'No entry in pubKeyCredParams was of type "public-key"',
        'ERROR_MALFORMED_PUBKEYCREDPARAMS',
      )
    }
    return wrap(
      'No available authenticator supported any of the specified pubKeyCredParams algorithms',
      'ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG',
    )
  }

  if (error.name === 'SecurityError') {
    const host = typeof window !== 'undefined' ? window.location.hostname : ''
    const rpId = publicKey.rp.id ?? host
    // RP ID pode ser o hostname ou um sufixo registrável (ex.: synap.med.br em app.synap.med.br).
    const rpMatchesHost = host === rpId || (rpId.length > 0 && host.endsWith(`.${rpId}`))

    if (!host || !/^(localhost|([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,})$/i.test(host)) {
      return wrap(`${host} is an invalid domain`, 'ERROR_INVALID_DOMAIN')
    }
    if (!rpMatchesHost) {
      return wrap(`The RP ID "${rpId}" is invalid for this domain`, 'ERROR_INVALID_RP_ID')
    }
    // SecurityError com RP ID válido (ex.: Permissions-Policy) — não degradar a Non-Webauthn.
    return wrap(
      error.message || 'Security error during WebAuthn registration',
      'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY',
      'SecurityError',
    )
  }

  if (error.name === 'TypeError') {
    const userId = publicKey.user.id
    const byteLength =
      userId instanceof ArrayBuffer
        ? userId.byteLength
        : ArrayBuffer.isView(userId)
          ? userId.byteLength
          : undefined
    if (byteLength !== undefined && (byteLength < 1 || byteLength > 64)) {
      return wrap('User ID was not between 1 and 64 characters', 'ERROR_INVALID_USER_ID_LENGTH')
    }
    return wrap(
      error.message || 'TypeError during WebAuthn registration',
      'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY',
      'TypeError',
    )
  }

  if (error.name === 'UnknownError') {
    return wrap(
      'The authenticator was unable to process the specified options, or could not create a new credential',
      'ERROR_AUTHENTICATOR_GENERAL_ERROR',
    )
  }

  // Fallback: preserve cause name/message instead of opaque Non-Webauthn-only string.
  return wrap(
    error.message?.trim()
      ? error.message
      : 'a Non-Webauthn related error has occurred',
    'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY',
    error.name || 'Unknown Error',
  )
}

export const createPasskeyCredential = async (
  publicKey: PublicKeyCredentialCreationOptions,
  signal?: AbortSignal,
): Promise<{ data: PublicKeyCredential | null; error: IdentifiedPasskeyError | null }> => {
  try {
    const response = await navigator.credentials.create({
      publicKey,
      signal,
    })
    if (!response) {
      const empty = new Error('Empty credential response') as IdentifiedPasskeyError
      empty.name = 'WebAuthnUnknownError'
      empty.code = 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY'
      return { data: null, error: empty }
    }
    if (!(response instanceof PublicKeyCredential)) {
      const unexpected = new Error('Browser returned unexpected credential type') as IdentifiedPasskeyError
      unexpected.name = 'WebAuthnUnknownError'
      unexpected.code = 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY'
      return { data: null, error: unexpected }
    }
    return { data: response, error: null }
  } catch (err) {
    return {
      data: null,
      error: identifyPasskeyRegistrationError(err as Error, publicKey, signal),
    }
  }
}
